import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CustomColumnTypes } from "@/enum/list.enum";
import { useGetColumnTemplatesWithAiSnippetQuery } from "@/pages/Private/redux/list/list.api";
import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";
import { useTemplateColumnInsert } from "@/pages/Private/pages/Messages/components/AiSnippets/hooks/useTemplateColumnInsert";
import {
	buildSnippetRows,
	normalizeSnippetName,
} from "@/pages/Private/pages/Messages/components/AiSnippets/utils/snippetRows";
import { buildSnippetToken } from "@/pages/Private/pages/Messages/components/AiSnippets/utils/snippetTokens";

import { FooterPopoverItem, FooterPopoverSection } from "../utils/editorFooter.types";

interface UseAiSnippetFooterItemsArgs {
	listColumns?: ColumnConfig[];
	listId?: number;
	search: string;
	onInsert: (token: string) => boolean;
	onCreateNew?: () => void;
	/** Snapshot the editor cursor before the async template insert starts. */
	captureCursor: () => void;
}

/**
 * AI snippet sections of the footer popover: the list's AI_SNIPPET columns
 * (insertable as-is) plus company-wide templates (persisted as a column
 * first, then inserted — the row shows a spinner while in flight).
 */
export const useAiSnippetFooterItems = ({
	listColumns,
	listId,
	search,
	onInsert,
	onCreateNew,
	captureCursor,
}: UseAiSnippetFooterItemsArgs): FooterPopoverSection[] => {
	const { t } = useTranslation();

	const { data: templatesResponse } = useGetColumnTemplatesWithAiSnippetQuery({
		page: 1,
		limit: 1000,
	});

	const aiColumns = useMemo(
		() => listColumns?.filter((column) => column.type === CustomColumnTypes.AI_SNIPPET) ?? [],
		[listColumns]
	);

	const { insertTemplate, pendingKey, createdColumns } = useTemplateColumnInsert({
		listId,
		captureCurrentPosition: captureCursor,
		onInsertToken: onInsert,
	});

	const rows = useMemo(
		() =>
			buildSnippetRows({
				listColumns: aiColumns,
				createdColumns,
				templates: templatesResponse?.data ?? [],
				insertedColumnIds: [],
			}),
		[aiColumns, createdColumns, templatesResponse]
	);

	return useMemo(() => {
		const normalizedSearch = normalizeSnippetName(search);

		const items: FooterPopoverItem[] = rows
			.filter((row) => !normalizedSearch || row.name.toLowerCase().includes(normalizedSearch))
			.map((row) =>
				row.kind === "listColumn"
					? {
							key: row.key,
							name: row.name,
							onSelect: () => onInsert(buildSnippetToken(row.columnId, row.name)),
						}
					: {
							key: row.key,
							name: row.name,
							meta: t("aiSnippet.section.template"),
							pending: pendingKey === row.key,
							onSelect: () => void insertTemplate(row.template),
						}
			);

		const sections: FooterPopoverSection[] = [
			{ label: t("editorFooter.sections.available"), items },
		];

		if (onCreateNew) {
			sections.push({
				label: t("editorFooter.sections.create"),
				items: [
					{
						key: "create-ai-snippet",
						name: t("editorFooter.newAiSnippet"),
						isCreateAction: true,
						onSelect: onCreateNew,
					},
				],
			});
		}

		return sections;
	}, [rows, search, onInsert, onCreateNew, insertTemplate, pendingKey, t]);
};
